import { ColorPicker, Input } from 'antd'
import { AggregationColor } from 'antd/es/color-picker/color'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { useZonePropertyAnchor } from 'features/libra/stepComponents/zoneAnalysis/useZonePropertyAnchor'
import { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { isMissingPrefillValue } from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import { FC } from 'react'
import styled from 'styled-components'

import { AiPrefillFieldState } from './AiPrefillFieldState'

const { TextArea } = Input

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const HelpText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-style: italic;
`

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: end;
`

export interface ZoneDescriptionPanelProps {
  zone: ZoneParams
  onChange: (updates: Partial<ZoneParams>) => void
  readonly?: boolean
}

export const ZoneDescriptionPanel: FC<ZoneDescriptionPanelProps> = ({
  zone,
  onChange,
  readonly,
}) => {
  const anchor = useZonePropertyAnchor(zone.id)

  const handleColorChange = (value: AggregationColor) => {
    onChange({ color: value.toHexString() })
  }

  return (
    <Panel>
      <CommentIndicator anchor={anchor('title')}>
        <FieldRow>
          <FieldGroup>
            <Label>Zone Name *</Label>
            <Input
              value={zone.title}
              onChange={(e) => onChange({ title: e.target.value })}
              placeholder='e.g., Zone 1: Bottom Section'
              disabled={readonly}
              addonAfter={
                <ColorPicker
                  value={zone.color || '#3B82F6'}
                  onChange={handleColorChange}
                  disabled={readonly}
                />
              }
            />
          </FieldGroup>
        </FieldRow>
      </CommentIndicator>

      <CommentIndicator anchor={anchor('description')}>
        <FieldGroup>
          <Label>
            Zone Description
            <AiPrefillFieldState
              meta={zone.ai_prefill?.basic_info}
              field='description'
              empty={isMissingPrefillValue('description', zone.description)}
            />
          </Label>
          <HelpText>
            Provide a detailed description of this zone, including key features,
            location, and any special considerations
          </HelpText>
          <TextArea
            value={zone.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder='e.g., Bottom section including head, nozzles, and liquid level indication. Expected to experience more aggressive degradation due to liquid phase contact.'
            rows={4}
            disabled={readonly}
          />
        </FieldGroup>
      </CommentIndicator>

      <CommentIndicator anchor={anchor('review_notes')}>
        <FieldGroup>
          <Label>
            Review Notes
            <AiPrefillFieldState
              meta={zone.ai_prefill?.basic_info}
              field='review_notes'
              empty={isMissingPrefillValue('review_notes', zone.review_notes)}
            />
          </Label>
          <HelpText>Internal notes about this zone analysis</HelpText>
          <TextArea
            value={zone.review_notes}
            onChange={(e) => onChange({ review_notes: e.target.value })}
            placeholder='Add any review notes, assumptions, or validation requirements...'
            rows={3}
            disabled={readonly}
          />
        </FieldGroup>
      </CommentIndicator>

      <CommentIndicator anchor={anchor('assumptions')}>
        <FieldGroup>
          <Label>
            Zone-Specific Assumptions
            <AiPrefillFieldState
              meta={zone.ai_prefill?.basic_info}
              field='assumptions'
              empty={isMissingPrefillValue('assumptions', zone.assumptions)}
            />
          </Label>
          <HelpText>
            List any assumptions made for this zone (one per line)
          </HelpText>
          <TextArea
            value={zone.assumptions?.join('\n')}
            onChange={(e) =>
              onChange({ assumptions: e.target.value.split('\n') })
            }
            onBlur={(e) =>
              onChange({
                assumptions: e.target.value
                  .split('\n')
                  .filter((a) => a.trim().length > 0),
              })
            }
            placeholder='e.g., Damage mechanism assignment is accurate&#10;No additional contaminants expected&#10;Similar service to reference vessels'
            rows={4}
            disabled={readonly}
          />
        </FieldGroup>
      </CommentIndicator>

      <CommentIndicator anchor={anchor('zone_recommendations')}>
        <FieldGroup>
          <Label>
            Zone-Specific Recommendations
            <AiPrefillFieldState
              meta={zone.ai_prefill?.basic_info}
              field='zone_recommendations'
              empty={isMissingPrefillValue(
                'zone_recommendations',
                zone.zone_recommendations
              )}
            />
          </Label>
          <HelpText>
            Inspection recommendations specific to this zone (one per line)
          </HelpText>
          <TextArea
            value={zone.zone_recommendations?.join('\n')}
            onChange={(e) =>
              onChange({
                zone_recommendations: e.target.value.split('\n'),
              })
            }
            onBlur={(e) =>
              onChange({
                zone_recommendations: e.target.value
                  .split('\n')
                  .filter((r) => r.trim().length > 0),
              })
            }
            placeholder='e.g., Focus on bottom head where solids accumulate&#10;Take equal distribution of nozzle thickness readings&#10;Check flange faces for damage'
            rows={4}
            disabled={readonly}
          />
        </FieldGroup>
      </CommentIndicator>
    </Panel>
  )
}
